import { useState, type FormEvent } from "react";
import { useLocale } from "@hooks/useLocale";
import { TENANT_BRANDS, DEFAULT_TENANT } from "@oxbo/tokens";
import { loginProdWith, loginAsDemo } from "@api/prodAuth";
import "./auth.css";

/**
 * Login screen shown by the render-gate when prod-auth is enabled and the app
 * holds no token (i.e. the user has logged out, or a login is needed). Collects
 * email + password and calls `loginProdWith`; on success it reloads so the app
 * boots the normal way with a fresh token (and the manual-logout flag cleared).
 *
 * A secondary "continue as demo" action clears the manual-logout flag and
 * reloads, which lets `initProdAuth` auto-log-in the demo user again — this is
 * how a first-time visitor still lands straight in the demo.
 *
 * Styled with the shared design tokens so it matches the app chrome. It shows
 * the OXBO wordmark (the login screen pre-dates any tenant preference, so it
 * uses the default tenant brand).
 */
export function LoginScreen() {
  const { t } = useLocale();
  const brand = TENANT_BRANDS[DEFAULT_TENANT];

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await loginProdWith(email.trim(), password);
      // Reload: boot the app the normal way with the fresh token in memory
      // (restored from sessionStorage) and the manual-logout flag cleared.
      window.location.reload();
    } catch {
      setError(t("auth.login.error"));
      setBusy(false);
    }
  }

  async function onContinueAsDemo() {
    if (busy || demoBusy) return;
    setError(null);
    setDemoBusy(true);
    try {
      // Demo is now an explicit action (the app no longer auto-logs-in demo).
      await loginAsDemo();
      window.location.reload();
    } catch {
      setError(t("auth.login.error"));
      setDemoBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={onSubmit} aria-labelledby="auth-title">
        <div className="auth-brand">
          <span className="auth-mark" aria-hidden="true">
            {brand.initial}
          </span>
          <span className="auth-wordmark">{brand.wordmark}</span>
        </div>

        <h1 id="auth-title" className="auth-title">
          {t("auth.login.title")}
        </h1>
        <p className="auth-subtitle">{t("auth.login.subtitle")}</p>

        <label className="auth-field">
          <span className="auth-label">{t("auth.login.email")}</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>

        <label className="auth-field">
          <span className="auth-label">{t("auth.login.password")}</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="auth-submit" disabled={busy}>
          {busy ? t("auth.login.submitting") : t("auth.login.submit")}
        </button>

        <div className="auth-divider" aria-hidden="true" />

        <button
          type="button"
          className="auth-demo"
          onClick={() => void onContinueAsDemo()}
          disabled={busy || demoBusy}
        >
          {demoBusy ? t("auth.login.submitting") : t("auth.login.continueDemo")}
        </button>
      </form>
    </div>
  );
}
